import { cloneElement, useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import type { FunctionComponent, ReactElement } from 'react';

import { useParams } from '../../../contexts/RouterContext';
import { useCurrentProject } from '../../../hooks/api/projects/useCurrentProject';
import { getProjectQueryKey } from '../../../hooks/api/projects/useGetProject';
import queryClient from '../../../ReactQuery/client';
import NavigationStore from '../../../redux/navigation';
import { capitalizeWord, getProjectBaseURLFromParams } from '../../../utils';
import Loader from '../../generic/Loader';
import NotFound from '../../generic/NotFound';
import Redirect from '../../generic/Redirect';
import { getLatestRevisionIdFromProject } from '../shared/helpers/getLatestRevisionIdFromProject';
import type { RevisionRouteParams } from '../projectDashboardTypes';

type Props = {
  children?: ReactElement;
};

const ProjectOverviewPage: FunctionComponent<Props> = ({
  children,
}) => {
  const dispatch = useDispatch();
  const params = useParams<RevisionRouteParams>();
  // Use isFetching (not isLoading) to match the previous behavior where loading was true during any fetch,
  // not just the initial load when there's no cached data.
  const { data: project, isFetching } = useCurrentProject();

  const locator = project?.locator ?? '';
  const onProjectChange = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: getProjectQueryKey(locator) });
  }, [locator]);

  // Create a compatibility wrapper for children that still expect the old IAsync format
  const projectForChildren = useMemo(() => ({
    data: project,
    status: { loading: isFetching },
  }), [project, isFetching]);

  // Branch provided in url must match a project branch to continue forward
  const doesBranchInUrlExist = useMemo(() => project?.references?.some(
    r => r.type === params.reference_type && r.name === params.reference,
  ) || false, [project?.references, params.reference_type, params.reference]);

  if (!project) {
    return (
      <div className="container margin-top">
        <Loader />
      </div>
    );
  }

  if (!doesBranchInUrlExist) {
    // Show the branch dropdown in the nav as a visual cue
    dispatch(
      NavigationStore.actions.setNavState({
        expand: 'project-expanded',
        navProps: {
          showBranchDropdown: true,
          shouldUseCallToAction: true,
        },
      }),
    );
    return <NotFound heading={`${capitalizeWord(params.reference_type) || 'Branch'} "${params.reference}" not found`} />;
  }

  if (children) {
    return cloneElement(
      children,
      {
        project: projectForChildren,
        onProjectChange,
      },
    );
  }

  // If a user hits this page directly, AND the branch they provided exists, try to redirect
  if (!children) {
    const revisionId = getLatestRevisionIdFromProject(project);
    if (revisionId) {
      return (
        <Redirect
          replace
          to={getProjectBaseURLFromParams({
            ...params,
            revision_id: revisionId,
          })}
        />
      );
    }
  }

  return <NotFound />;
};

export default ProjectOverviewPage;
